/**
 * SEO Configuration and Utilities
 * Provides route-specific meta tags and Open Graph data
 */

import { ASSET_VERSIONS, getVersionedAsset } from './constants';

const BASE_URL = 'https://try.solidly.ai';
const SITE_NAME = 'SolidlyAI';

// Default meta tags for the site
export const defaultMeta = {
  title: 'SolidlyAI - AI & Data Implementation Specialists',
  description: 'Stop talking about AI, and start building. SolidlyAI is a founder-led AI & data implementation company. We help companies turn vague AI ambitions into real-world systems that deliver measurable impact.',
  keywords: 'AI consulting, AI implementation, data engineering, machine learning consulting, AI strategy, applied AI, enterprise AI, data analytics consulting, AI product development, production AI',
  ogImage: `${BASE_URL}${getVersionedAsset('/og-image.jpg', ASSET_VERSIONS.OG_IMAGE)}`,
  twitterImage: `${BASE_URL}${getVersionedAsset('/twitter-image.jpg', ASSET_VERSIONS.TWITTER_IMAGE)}`,
};

// Route-specific SEO data
export const routeSEO = {
  '/': {
    title: 'SolidlyAI - AI & Data Implementation Specialists',
    description: 'Stop talking about AI, and start building. We are a founder-led AI & data implementation company. We help companies turn vague AI ambitions into real-world systems that deliver measurable impact.',
    keywords: 'AI consulting, AI implementation, data engineering, machine learning consulting, AI strategy, applied AI, enterprise AI',
    canonical: BASE_URL,
  },
  '/services': {
    title: 'AI & Data Implementation Services - SolidlyAI',
    description: 'Data & Analytics Engineering, Applied AI & Machine Learning, and Productization & Prototyping. Full-stack AI delivery from strategy to production deployment.',
    keywords: 'AI consulting services, data engineering, machine learning services, AI implementation, AI product development',
    canonical: `${BASE_URL}/services`,
  },
  '/projects': {
    title: 'AI & Data Implementation Services - SolidlyAI',
    description: 'Data & Analytics Engineering, Applied AI & Machine Learning, and Productization & Prototyping. Full-stack AI delivery from strategy to production deployment.',
    keywords: 'AI consulting services, data engineering, machine learning services, AI implementation, AI product development',
    canonical: `${BASE_URL}/projects`,
  },
};

/**
 * Get SEO metadata for a specific route
 * @param {string} pathname - The current route pathname
 * @returns {object} SEO metadata object
 */
export const getSEOForRoute = (pathname) => {
  const routeData = routeSEO[pathname] || routeSEO['/'];
  
  return {
    title: routeData.title,
    description: routeData.description,
    keywords: routeData.keywords || defaultMeta.keywords,
    canonical: routeData.canonical,
    ogTitle: routeData.ogTitle || routeData.title,
    ogDescription: routeData.ogDescription || routeData.description,
    ogImage: routeData.ogImage || defaultMeta.ogImage,
    twitterImage: routeData.twitterImage || defaultMeta.twitterImage,
  };
};

/**
 * Generate structured data for the organization
 * @returns {object} JSON-LD structured data
 */
export const getOrganizationSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'Organization',
  '@id': `${BASE_URL}#organization`,
  name: SITE_NAME,
  legalName: 'SolidlyAI',
  description: 'Founder-led AI & data implementation company. We help companies turn vague AI ambitions into real-world systems that deliver measurable impact.',
  url: BASE_URL,
  logo: `${BASE_URL}/logo192.png`,
  image: defaultMeta.ogImage,
  founder: {
    '@type': 'Person',
    name: 'Alek Hagopian',
    jobTitle: 'Founder & AI Implementation Expert',
    description: 'Former Mastercard Senior Managing Consultant and AI founder with 8+ years in AI and 10+ years in data. Built and sold AI company in 2021.',
  },
  areaServed: 'Worldwide',
  serviceType: [
    'AI Implementation',
    'Machine Learning Consulting',
    'Data Engineering',
    'Analytics Consulting',
    'AI Product Development'
  ],
  address: {
    '@type': 'PostalAddress',
    addressRegion: 'Virginia',
    addressCountry: 'US'
  },
  contactPoint: {
    '@type': 'ContactPoint',
    email: 'alek@solidly.ai',
    contactType: 'Customer Service',
    availableLanguage: 'English'
  },
  sameAs: [
    'https://www.linkedin.com/company/solidlyai',
  ]
});

/**
 * Generate FAQ structured data for rich search results
 * @returns {object} JSON-LD FAQ schema
 */
export const getFAQSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'What makes SolidlyAI different from other AI consulting firms?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'SolidlyAI is founder-led by Alek Hagopian, who has 8+ years in AI and built and sold an AI company. We focus on implementation and measurable outcomes, not just strategy decks. Our experience spans Fortune 500 companies like Mastercard to early-stage startups.'
      }
    },
    {
      '@type': 'Question',
      name: 'What types of AI and data projects does SolidlyAI work on?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'We specialize in three areas: Data & Analytics Engineering (modern data warehouses, pipelines, governance), Applied AI & Machine Learning (predictive models, AI copilots, NLP systems), and Productization & Prototyping (MVP AI apps, human-AI interfaces, enterprise solutions).'
      }
    },
    {
      '@type': 'Question',
      name: 'Do you work with Fortune 500 companies or startups?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Both. Our experience includes enterprise work with companies like Mastercard, Hart Energy, and Crain Communications, as well as startup AI product development. We adapt our approach to fit your organization\'s size and needs.'
      }
    },
    {
      '@type': 'Question',
      name: 'What is your approach to AI consulting?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'We help companies turn vague AI ambitions into real-world systems that deliver measurable impact. Our principles include leaving everything more organized than we found it, communicating progress early and often, sharing transparent documentation, and delivering measurable outcomes.'
      }
    },
    {
      '@type': 'Question',
      name: 'How do I get started with SolidlyAI?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Contact us at alek@solidly.ai for an initial consultation. We\'ll discuss your AI strategy, current challenges, and provide practical next steps focused on implementation.'
      }
    }
  ]
});

/**
 * Generate Organization structured data for Knowledge Panel
 * @returns {object} JSON-LD Organization schema
 */
export const getOrganizationKnowledgePanelSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'ProfessionalService',
  name: 'SolidlyAI',
  alternateName: 'SolidlyAI Consulting',
  url: BASE_URL,
  logo: `${BASE_URL}/logo192.png`,
  image: defaultMeta.ogImage,
  description: 'Founder-led AI & data implementation company. We help companies turn vague AI ambitions into real-world systems that deliver measurable impact.',
  priceRange: '$$',
  serviceArea: {
    '@type': 'Place',
    name: 'Worldwide'
  },
  hasOfferCatalog: {
    '@type': 'OfferCatalog',
    name: 'AI Implementation Services',
    itemListElement: [
      {
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: 'Data & Analytics Engineering',
          description: 'Modern data warehouse architecture, governance, and real-time pipelines'
        }
      },
      {
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: 'Applied AI & Machine Learning',
          description: 'Production-ready AI systems, predictive modeling, and AI copilots'
        }
      },
      {
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: 'Productization & Prototyping',
          description: 'MVP AI app design, human-AI interfaces, and enterprise AI solutions'
        }
      }
    ]
  },
  contactPoint: {
    '@type': 'ContactPoint',
    email: 'alek@solidly.ai',
    contactType: 'Customer Support'
  },
  sameAs: [
    'https://www.linkedin.com/company/solidlyai',
  ]
});
